import { commonFilter } from '../type';
import { Segment } from 'tea-component';
import { chartAllColors } from '@op/constants';
import { DBO_EC, DBO_V, DBO_Y } from '@op/utils/format';
import useLanguage from '@op/hooks/useLanguage';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useCoreKeyMetricsChart } from '../hooks';
import { getXAxisAxisLabel } from '@op/utils/chart';
import EChartCommon from '@op/components/EChartCommon';
import {
  keyMetricsArray,
  KeyMetricsItemType,
  MetricListType,
} from '../constants';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { ChartEventContext } from '@op/pages/Detail/component/chartEvent';

const getValueSign = (value: number): string => {
  if (value > 0) return '+';
  if (value < 0) return '-';
  return '';
};

export function KeyMetricsChart({
  height,
  filter,
}: {
  height: number;
  filter: commonFilter;
}) {
  const { formatMessage } = useLanguage();
  const [activeItem, setActiveKeyItem] = useState<KeyMetricsItemType>(
    keyMetricsArray[0],
  );

  const options = useMemo(() => {
    return keyMetricsArray.map((item) => ({
      value: item.key,
      text: formatMessage(item.text),
    }));
  }, [formatMessage, keyMetricsArray]);

  const { getEventsDict, eventsDict, changeDetailPage } =
    useContext(ChartEventContext);

  const { onClick } = useMemo(() => {
    return getEventsDict?.('table') || {};
  }, [getEventsDict]);

  return (
    <div className="key-metrics-chart-wrapper">
      <Segment
        className={`card-slot-segment`}
        value={activeItem.key}
        onChange={(value) => {
          const foundItem = keyMetricsArray.find((item) => item.key === value);
          if (foundItem) {
            setActiveKeyItem(foundItem);
            changeDetailPage?.({ metric: value }, 'props');
          }
        }}
        options={options}
      />
      <KeyMetricsChartItem
        height={height}
        filter={filter}
        item={activeItem}
        onClick={onClick}
        eventsDict={eventsDict}
      ></KeyMetricsChartItem>
    </div>
  );
}

function KeyMetricsChartItem({
  item,
  height,
  filter,
  eventsDict,
  onClick,
}: {
  height: number;
  filter: commonFilter;
  item: KeyMetricsItemType;
  eventsDict: Record<string, unknown>;
  onClick: (params: { xAxis: string }) => void;
}) {
  const { formatMessage } = useLanguage();
  const { chartData, loading } = useCoreKeyMetricsChart({
    metric: item.key,
    filter,
  });

  const { getBottomClickBtn, divElementRef } = useChartClickInfo({ onClick });

  const options = useMemo(() => {
    if (chartData?.isEmpty) {
      return {};
    }
    const { xAxis, seriesObj, isMin } = chartData;

    const option = {
      tooltip: {
        trigger: 'axis',
        borderWidth: 0,
        padding: 0,
        enterable: true,
        confine: true,
        extraCssText: 'z-index:8',
        formatter: (data: any[]) => {
          if (!data || data.length === 0) return '';
          const { axisValue, dataIndex, name } = data[0] || {};
          return formatChartParamsDate({
            params: data,
            title: listTimeTitleFunc(axisValue, 'weekly'),
            valueFormat: item.format,
            listTitleFunc: (item: any) => formatMessage(item.seriesName),
            footerText: getBottomClickBtn(name),
            dealwithRecords: (records) => {
              const value = seriesObj?.val_and_5week_Rate?.[dataIndex] || 0;

              records.push({
                text: formatMessage('Compared with Average'),
                value: `${getValueSign(value)}${DBO_V.percentageFormat(
                  value,
                  '-',
                )}`,
                originValue: 0,
                originText: '本周相比均值',
              });

              return records;
            },
          });
        },
        position: (
          position: any,
          params: any,
          dom: any,
          rect: any,
          size: any,
        ) => {
          return DBO_EC.position(
            position,
            params,
            dom,
            rect,
            size,
            80,
            -20,
            'top',
          );
        },
      },
      legend: {
        data: [
          {
            name: item.text,
            itemStyle: { color: chartAllColors[0] },
          },
          {
            name: 'Past 5-week Average',
            itemStyle: { color: '#EF5858' },
          },
        ],
        formatter: (value: string) => {
          return formatMessage(value);
        },
      },
      xAxis: {
        type: 'category',
        boundaryGap: ['20%', 0],
        data: xAxis || [],
        axisLabel: {
          ...getXAxisAxisLabel({ date_type: 'daily' }),
          fontSize: 10,
          showMaxLabel: true,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) =>
            DBO_Y.reviewsPercent(value, item.isPercentage, isMin),
        },
      },
      series: [
        {
          name: item.text,
          type: 'line',
          itemStyle: {
            color: chartAllColors[0],
          },
          data: seriesObj?.val || [],
        },
        {
          name: 'Past 5-week Average',
          type: 'line',
          itemStyle: {
            color: '#EF5858',
          },
          lineStyle: {
            type: 'dashed',
          },
          data: seriesObj?.Past_5_week_Average,
        },
      ],
      grid: {
        top: 20,
      },
    };
    return option;
  }, [chartData, formatMessage]);

  return (
    <div ref={divElementRef} style={{ position: 'relative' }}>
      <EChartCommon
        loading={loading}
        loadingHeight={height}
        height={height}
        eventsDict={eventsDict}
        options={options}
      ></EChartCommon>
    </div>
  );
}

export function useChartClickInfo({ onClick }: { onClick: any }) {
  const { formatMessage } = useLanguage();

  function getBottomClickBtn(time: string) {
    return `<div class="chart-common-formatter-links" data-url="feeds" data-xAxis="${time}" >
                ${formatMessage({
                  id: 'Click to view channel & language distribution',
                })}
              </div>
            `;
  }

  const divElementRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function clickEventItem(event: any) {
      const { target } = event;
      const xAxis = target?.getAttribute('data-xAxis');

      if (!xAxis) {
        return;
      }
      onClick?.({ xAxis });
    }

    if (divElementRef.current) {
      divElementRef.current.addEventListener('click', clickEventItem);
    }

    return () => {
      if (divElementRef.current) {
        divElementRef.current.removeEventListener('click', clickEventItem);
      }
    };
  }, []);

  return {
    getBottomClickBtn,
    divElementRef,
  };
}
